<template>
  <div class="sleep-item-creator">
    <div class="creator-header">
      <h1 class="text-2xl font-bold text-gray-800">{{ editingId ? 'Edit Sleep Content' : 'Create New Sleep Content' }}</h1>
      <div class="header-actions">
        <button @click="goBack" class="btn-secondary">
          <i class="icon-arrow-left"></i> Back
        </button>
        <button 
          @click="saveContent" 
          class="btn-primary"
          :disabled="!isFormValid || saving"
        >
          <i v-if="saving" class="icon-spinner animate-spin"></i>
          <i v-else class="icon-save"></i>
          {{ saving ? 'Saving...' : (editingId ? 'Update' : 'Save') }}
        </button>
      </div>
    </div>

    <!-- Progress indicator -->
    <div class="progress-indicator">
      <div class="progress-steps">
        <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <div class="step-label">Basic Info</div>
        </div>
        <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <div class="step-label">Media</div>
        </div>
        <div class="step" :class="{ active: currentStep >= 3 }">
          <div class="step-number">3</div>
          <div class="step-label">Review</div>
        </div>
      </div>
    </div>

    <!-- Form error message -->
    <div v-if="error" class="error-banner">
      <i class="icon-error"></i>
      <span>{{ error }}</span>
      <button @click="error = null" class="close-error">
        <i class="icon-close"></i>
      </button>
    </div>

    <!-- Step 1: Basic Information -->
    <div v-if="currentStep === 1" class="form-step">
      <div class="step-content">
        <h2 class="step-title">Basic Information</h2>
        
        <div class="form-grid">
          <div class="form-group">
            <label for="title" class="form-label">Title</label>
            <input 
              id="title"
              v-model="formData.title" 
              type="text" 
              placeholder="Enter content title"
              class="form-input"
              :class="{ 'error': errors.title }"
            />
            <span v-if="errors.title" class="error-text">{{ errors.title }}</span>
          </div>
          
          <div class="form-group">
            <label for="title_ar" class="form-label">Arabic Title</label>
            <input 
              id="title_ar"
              v-model="formData.title_ar" 
              type="text" 
              placeholder="Enter Arabic title"
              class="form-input"
              dir="rtl"
              :class="{ 'error': errors.title_ar }"
            />
            <span v-if="errors.title_ar" class="error-text">{{ errors.title_ar }}</span>
          </div>
          
          <div class="form-group">
            <label for="type" class="form-label">Content Type <span class="required">*</span></label>
            <select 
              id="type"
              v-model="formData.type" 
              class="form-input"
              :class="{ 'error': errors.type }"
            >
              <option value="bedtime">Bedtime Story</option>
              <option value="breathing">Breathing Exercise</option>
              <option value="sleep_frequency">Sleep Frequency</option>
              <option value="souds_of_nature">Sounds of Nature</option>
              <option value="brain_programming">Brain Programming</option>
            </select>
            <span v-if="errors.type" class="error-text">{{ errors.type }}</span>
          </div>
          
          <div class="form-group full-width">
            <label for="description" class="form-label">Description</label>
            <div class="quill-wrapper" :class="{ 'error': errors.details }">
              <QuillEditor
                v-model:content="formData.details"
                contentType="html"
                placeholder="Enter content description..."
                :toolbar="toolbarOptions"
                theme="snow"
                style="min-height: 120px;"
              />
            </div>
            <span v-if="errors.details" class="error-text">{{ errors.details }}</span>
          </div>
          
          <div class="form-group full-width">
            <label for="description_ar" class="form-label">Arabic Description</label>
            <div class="quill-wrapper rtl" :class="{ 'error': errors.details_ar }">
              <QuillEditor
                v-model:content="formData.details_ar"
                contentType="html"
                placeholder="أدخل وصف المحتوى..."
                :toolbar="arabicToolbarOptions"
                theme="snow"
                style="min-height: 120px; direction: rtl; text-align: right;"
              />
            </div>
            <span v-if="errors.details_ar" class="error-text">{{ errors.details_ar }}</span>
          </div>
        </div>
        
        <div class="step-actions">
          <button @click="nextStep" class="btn-primary" :disabled="!isStep1Valid">
            Continue <i class="icon-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Step 2: Media Content -->
    <div v-if="currentStep === 2" class="form-step">
      <div class="step-content">
        <h2 class="step-title">Media Content</h2>
        
        <div class="media-grid">
          <!-- Images Section -->
          <div class="media-section">
            <h3 class="media-title">Images</h3>
            
            <!-- Cover Image -->
            <div class="image-upload-group">
              <label class="form-label">Cover Image</label>
              <FileUploader
                :api-url="apiUrl"
                :title="'Upload Cover Image'"
                :show-title="true"
                :multiple="false"
                :accept="'image/*'"
                :show-files-list="true"
                :auto-upload="true"
                :image-preview-mode="false"
                :allow-copy="true"
                :support-text="'Upload cover image - URL will be automatically filled below'"
                @files-uploaded="handleCoverImageUploaded"
              />
              
              <div class="form-group mt-2">
                <input 
                  v-model="formData.cover_photo" 
                  type="url" 
                  placeholder="Cover image URL"
                  class="form-input"
                  :class="{ 'error': errors.cover_photo }"
                />
                <span v-if="errors.cover_photo" class="error-text">{{ errors.cover_photo }}</span>
              </div>
              
              <div v-if="formData.cover_photo" class="image-preview mt-2">
                <img :src="formData.cover_photo" :alt="formData.title" class="preview-img" />
              </div>
            </div>
            
            <!-- Main Photo -->
            <div class="image-upload-group mt-4">
              <label class="form-label">Main Photo</label>
              <FileUploader
                :api-url="apiUrl"
                :title="'Upload Main Photo'"
                :show-title="true"
                :multiple="false"
                :accept="'image/*'"
                :show-files-list="true"
                :auto-upload="true"
                :image-preview-mode="false"
                :allow-copy="true"
                :support-text="'Upload main photo - URL will be automatically filled below'"
                @files-uploaded="handleMainPhotoUploaded"
              />
              
              <div class="form-group mt-2">
                <input 
                  v-model="formData.main_photo" 
                  type="url" 
                  placeholder="Main photo URL"
                  class="form-input"
                  :class="{ 'error': errors.main_photo }"
                />
                <span v-if="errors.main_photo" class="error-text">{{ errors.main_photo }}</span>
              </div>
              
              <div v-if="formData.main_photo" class="image-preview mt-2">
                <img :src="formData.main_photo" :alt="formData.title" class="preview-img" />
              </div>
            </div>
          </div>
          
          <!-- Content Type Specific Media -->
          <div class="media-section">
            <!-- Bedtime Story Content -->
            <div v-if="formData.type === 'bedtime'" class="content-type-media">
              <h3 class="media-title">📚 Bedtime Story Audio</h3>
              <FileUploader
                :api-url="apiUrl"
                :title="'Upload Audio File'"
                :show-title="true"
                :multiple="false"
                :accept="'audio/*'"
                :auto-upload="true"
                :gallery-mode="false"
                :show-files-list="true"
                :allow-copy="true"
                :support-text="'Upload bedtime story audio - URL will be automatically filled below'"
                @files-uploaded="handleAudioFilesUploaded"
              />
              
              <div class="form-group mt-2">
                <label class="form-label">Audio URL <span class="required">*</span> <small>(Database required)</small></label>
                <input 
                  v-model="formData.audio_url" 
                  type="url" 
                  placeholder="Audio file URL"
                  class="form-input"
                  :class="{ 'error': errors.audio_url }"
                />
                <span v-if="errors.audio_url" class="error-text">{{ errors.audio_url }}</span>
              </div>
            </div>
            
            <!-- Breathing Exercise Content -->
            <div v-if="formData.type === 'breathing'" class="content-type-media">
              <h3 class="media-title">🧘‍♀️ Breathing Exercise</h3>
              
              <!-- Audio URL (Database Required) -->
              <div class="upload-group">
                <label class="form-label">Audio URL <span class="required">*</span> <small>(Database required)</small></label>
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Audio File'"
                  :show-title="true"
                  :multiple="false"
                  :accept="'audio/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :allow-copy="true"
                  :support-text="'Upload audio - URL will be automatically filled below'"
                  @files-uploaded="handleAudioFilesUploaded"
                />
                
                <div class="form-group mt-1">
                  <input 
                    v-model="formData.audio_url" 
                    type="url" 
                    placeholder="Audio file URL (required)"
                    class="form-input"
                    :class="{ 'error': errors.audio_url }"
                  />
                  <span v-if="errors.audio_url" class="error-text">{{ errors.audio_url }}</span>
                </div>
              </div>
              
              <!-- Video Over (Optional) -->
              <div class="upload-group mt-4">
                <label class="form-label">Video Content <small>(Optional)</small></label>
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Video File'"
                  :show-title="true"
                  :multiple="false"
                  :accept="'video/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :allow-copy="true"
                  :support-text="'Upload breathing exercise video - URL will be automatically filled below'"
                  @files-uploaded="handleVideoFilesUploaded"
                />
                
                <div class="form-group mt-1">
                  <input 
                    v-model="formData.video_over" 
                    type="url" 
                    placeholder="Video file URL (optional)"
                    class="form-input"
                    :class="{ 'error': errors.video_over }"
                  />
                  <span v-if="errors.video_over" class="error-text">{{ errors.video_over }}</span>
                </div>
              </div>
            </div>
            
            <!-- Advanced Content Types -->
            <div v-if="formData.type !== 'bedtime' && formData.type !== 'breathing'" class="content-type-media">
              <h3 class="media-title">
                <span v-if="formData.type === 'sleep_frequency'">🎵 Sleep Frequency Content</span>
                <span v-else-if="formData.type === 'souds_of_nature'">🌿 Sounds of Nature Content</span>
                <span v-else-if="formData.type === 'brain_programming'">🧠 Brain Programming Content</span>
                <span v-else>🎬 Advanced Content</span>
              </h3>
              
              <!-- Audio File (Database Required) -->
              <div class="upload-group">
                <label class="form-label">Audio File <span class="required">*</span> <small>(Database required)</small></label>
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Audio File'"
                  :show-title="true"
                  :multiple="false"
                  :accept="'audio/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :allow-copy="true"
                  :support-text="'Upload main audio track'"
                  @files-uploaded="handleAudioFilesUploaded"
                />
                
                <div class="form-group mt-1">
                  <input 
                    v-model="formData.audio_url" 
                    type="url" 
                    placeholder="Audio file URL (required)"
                    class="form-input"
                    :class="{ 'error': errors.audio_url }"
                  />
                  <span v-if="errors.audio_url" class="error-text">{{ errors.audio_url }}</span>
                </div>
              </div>
              
              <!-- Voice Over (Optional) -->
              <div class="upload-group mt-4">
                <label class="form-label">Voice Over <small>(Optional)</small></label>
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Voice Over'"
                  :show-title="true"
                  :multiple="false"
                  :accept="'audio/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :allow-copy="true"
                  :support-text="'Upload voice over narration'"
                  @files-uploaded="handleVoiceOverUploaded"
                />
                
                <div class="form-group mt-1">
                  <input 
                    v-model="formData.voice_over" 
                    type="url" 
                    placeholder="Voice over URL (optional)"
                    class="form-input"
                    :class="{ 'error': errors.voice_over }"
                  />
                  <span v-if="errors.voice_over" class="error-text">{{ errors.voice_over }}</span>
                </div>
              </div>
              
              <!-- Video Content (Optional) -->
              <div class="upload-group mt-4">
                <label class="form-label">Video Content <small>(Optional)</small></label>
                <FileUploader
                  :api-url="apiUrl"
                  :title="'Upload Video Content'"
                  :show-title="true"
                  :multiple="false"
                  :accept="'video/*'"
                  :auto-upload="true"
                  :gallery-mode="false"
                  :show-files-list="true"
                  :allow-copy="true"
                  :support-text="'Upload video content'"
                  @files-uploaded="handleVideoFilesUploaded"
                />
                
                <div class="form-group mt-1">
                  <input 
                    v-model="formData.video_over" 
                    type="url" 
                    placeholder="Video file URL (optional)"
                    class="form-input"
                    :class="{ 'error': errors.video_over }"
                  />
                  <span v-if="errors.video_over" class="error-text">{{ errors.video_over }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="step-actions">
          <button @click="prevStep" class="btn-secondary">
            <i class="icon-arrow-left"></i> Previous
          </button>
          <button @click="nextStep" class="btn-primary" :disabled="!isStep2Valid">
            Continue <i class="icon-arrow-right"></i>
          </button>
                      <div v-if="!isStep2Valid" class="mt-4 text-sm text-gray-600">
              <p v-if="!formData.cover_photo">• Cover image URL is required</p>
              <p v-if="formData.type !== 'breathing' && !formData.audio_url">• Primary {{ formData.type === 'breathing' ? 'video' : 'audio' }} URL is required</p>
            </div>
            
            <!-- Status info -->
            <div class="mt-4 p-4 bg-gray-100 rounded text-sm">
              <h4 class="font-semibold mb-2">🔍 Debug Status:</h4>
              <p><strong>Cover Photo URL:</strong> {{ formData.cover_photo ? '✅ Set' : '❌ Missing' }}</p>
              <p v-if="formData.cover_photo" class="text-xs text-gray-600 ml-4">{{ formData.cover_photo }}</p>
              
              <p><strong>Primary Audio URL:</strong> {{ formData.audio_url ? '✅ Set' : '❌ Missing' }}</p>
              <p v-if="formData.audio_url" class="text-xs text-gray-600 ml-4">{{ formData.audio_url }}</p>
              
              <p><strong>Content Type:</strong> {{ formData.type }}</p>
              <p><strong>Step 2 Valid:</strong> {{ isStep2Valid ? '✅ Yes' : '❌ No' }}</p>
              
              <p><strong>Validation Details:</strong></p>
              <ul class="ml-4 text-xs">
                <li>Has Cover Photo: {{ !!formData.cover_photo ? '✅' : '❌' }}</li>
                <li>Is Breathing Type: {{ formData.type === 'breathing' ? '✅' : '❌' }}</li>
                <li>Has Audio URL (non-breathing): {{ formData.type !== 'breathing' && !!formData.audio_url ? '✅' : '❌' }}</li>
              </ul>
            </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Review -->
    <div v-if="currentStep === 3" class="form-step">
      <div class="step-content">
        <h2 class="step-title">Review & Save</h2>
        
        <div class="review-content">
          <div class="review-card">
            <div class="review-header">
              <img 
                v-if="formData.cover_photo" 
                :src="formData.cover_photo" 
                :alt="formData.title"
                class="review-image"
              />
              <div v-else class="review-image-placeholder">
                <i class="icon-image"></i>
                <span>No cover image</span>
              </div>
              
              <div class="review-info">
                <h3 class="review-title">{{ formData.title }}</h3>
                <h4 class="review-title-ar">{{ formData.title_ar }}</h4>
                <div class="review-type">{{ getTypeLabel(formData.type) }}</div>
              </div>
            </div>
            
            <div class="review-details">
              <div v-if="formData.details" class="review-description">
                <strong>Description:</strong>
                <div class="html-content" v-html="formData.details"></div>
              </div>
              <div v-if="formData.details_ar" class="review-description">
                <strong>Arabic Description:</strong>
                <div class="html-content rtl-content" v-html="formData.details_ar"></div>
              </div>
              
              <div class="review-media">
                <strong>Media Files:</strong>
                <ul v-if="formData.audioFiles && formData.audioFiles.length > 0" class="media-list">
                  <li v-for="(file, index) in formData.audioFiles" :key="index">
                    <i class="icon-audio"></i>
                    {{ file.name || 'Media File' }}
                  </li>
                </ul>
                <span v-else class="no-media">No media files uploaded</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="step-actions">
          <button @click="prevStep" class="btn-secondary">
            <i class="icon-arrow-left"></i> Previous
          </button>
          <button 
            @click="saveContent" 
            class="btn-primary btn-large"
            :disabled="!isFormValid || saving"
          >
            <i v-if="saving" class="icon-spinner animate-spin"></i>
            <i v-else class="icon-save"></i>
            {{ saving ? 'Saving...' : (editingId ? 'Update Content' : 'Create Content') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast notification -->
    <div 
      v-if="toast.show" 
      class="toast-notification"
      :class="{ 
        'success': toast.type === 'success', 
        'error': toast.type === 'error',
        'info': toast.type === 'info'
      }"
    >
      <div class="toast-content">
        <i v-if="toast.type === 'success'" class="icon-success"></i>
        <i v-else-if="toast.type === 'error'" class="icon-error"></i>
        <i v-else class="icon-info"></i>
        <span>{{ toast.message }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import FileUploader from './FileUploader.vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

export default {
  name: 'SleepItemCreator',
  components: {
    FileUploader,
    QuillEditor
  },
  props: {
    editingId: {
      type: [String, Number],
      default: null
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    apiUrl: {
      type: String,
      default: 'https://r2-eu1.kewaarcdn.com/api/files'
    }
  },
  emits: ['save', 'cancel', 'back'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const saving = ref(false)
    const error = ref(null)
    const errors = ref({})
    
    const formData = ref({
      title: '',
      title_ar: '',
      type: 'bedtime',
      duration: 1,
      details: '',
      details_ar: '',
      cover_photo: '',
      main_photo: '',
      audio_url: '',
      voice_over: '',
      video_over: '',
      display_type: 'still',
      audioFiles: []
    })
    
    const toast = ref({
      show: false,
      message: '',
      type: 'success'
    })
    
    // Quill editor toolbar options
    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]
    
    const arabicToolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': ['', 'center', 'right', 'justify'] }],
      ['clean'],
      ['link']
    ]
    
    // Initialize form data if editing
    if (props.editingId && props.initialData) {
      formData.value = {
        ...formData.value,
        ...props.initialData,
        audioFiles: []
      }
      
      // Add existing audio files to display
      if (props.initialData.audio_url) {
        formData.value.audioFiles.push({
          name: 'Audio File',
          fullUrl: props.initialData.audio_url,
          url: props.initialData.audio_url,
          key: 'audio-file',
          size: 0
        })
      }
    }
    
    // Validation based on database constraints
    const validateStep1 = () => {
      errors.value = {}
      
      // Type is NOT NULL in database
      if (!formData.value.type) {
        errors.value.type = 'Content type is required (database constraint)'
      }
      
      // UI validation: at least one title recommended
      if (!formData.value.title.trim() && !formData.value.title_ar.trim()) {
        errors.value.title = 'At least one title (English or Arabic) is recommended'
      }
      
      return Object.keys(errors.value).length === 0
    }
    
    const isStep1Valid = computed(() => {
      return formData.value.type &&
             (formData.value.title.trim() || formData.value.title_ar.trim())
    })
    
    const isStep2Valid = computed(() => {
      // Database constraint: audio_url is NOT NULL
      return !!formData.value.audio_url
    })
    
    const isFormValid = computed(() => {
      return isStep1Valid.value && isStep2Valid.value
    })
    
    // Step navigation
    const nextStep = () => {
      if (currentStep.value === 1 && validateStep1()) {
        currentStep.value = 2
      } else if (currentStep.value === 2) {
        if (isStep2Valid.value) {
          currentStep.value = 3
        } else {
          // Show why step 2 is not valid
          const issues = []
          if (!formData.value.cover_photo) {
            issues.push('Cover image URL is required')
          }
          if (formData.value.type !== 'breathing' && !formData.value.audio_url) {
            issues.push(`Primary ${formData.value.type === 'breathing' ? 'video' : 'audio'} URL is required`)
          }
          if (issues.length > 0) {
            showToast(`Please complete the following: ${issues.join(', ')}`, 'error')
          }
        }
      }
    }
    
    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }
    
    const goBack = () => {
      emit('back')
    }
    
    // CDN URL construction
    const cdnBaseUrl = 'https://eu1.kewaarcdn.com/'
    
    const constructCdnUrl = (key) => {
      return cdnBaseUrl + key
    }
    
    // File upload handlers
    const handleCoverImageUploaded = (files) => {
      console.log('🔷 Cover image uploaded event fired!')
      console.log('🔷 Files received:', files)
      
      if (files && files.length > 0) {
        const file = files[0]
        console.log('🔷 First file object:', JSON.stringify(file, null, 2))
        
        // Extract key from the file object
        const key = file.key
        console.log('🔷 Extracted key:', key)
        
        if (key) {
          const cdnUrl = constructCdnUrl(key)
          console.log('🔷 Constructed CDN URL:', cdnUrl)
          
          // Set only the cover_photo URL
          formData.value.cover_photo = cdnUrl
          
          console.log('🔷 Cover photo updated:', {
            cover_photo: formData.value.cover_photo,
            isStep2Valid: isStep2Valid.value
          })
          
          showToast('✅ Cover image URL set automatically!', 'success')
        } else {
          console.error('❌ Could not extract key from file:', file)
          showToast('⚠️ Upload successful but could not extract key. Manual entry needed.', 'warning')
        }
      } else {
        console.error('❌ No files received in event')
      }
    }
    
    const handleMainPhotoUploaded = (files) => {
      console.log('🖼️ Main photo uploaded event fired!')
      
      if (files && files.length > 0) {
        const file = files[0]
        const key = file.key
        
        if (key) {
          const cdnUrl = constructCdnUrl(key)
          formData.value.main_photo = cdnUrl
          
          console.log('🖼️ Main photo updated:', formData.value.main_photo)
          showToast('✅ Main photo URL set automatically!', 'success')
        } else {
          showToast('⚠️ Upload successful but could not extract key. Manual entry needed.', 'warning')
        }
      }
    }
    
    const handleAudioFilesUploaded = (files) => {
      console.log('🎵 Audio files uploaded event fired!')
      
      if (files && files.length > 0) {
        const file = files[0]
        const key = file.key
        
        if (key) {
          const cdnUrl = constructCdnUrl(key)
          formData.value.audio_url = cdnUrl
          
          console.log('🎵 Audio URL updated:', formData.value.audio_url)
          showToast('✅ Audio URL set automatically!', 'success')
        } else {
          showToast('⚠️ Upload successful but could not extract key. Manual entry needed.', 'warning')
        }
      }
    }
    
    const handleVideoFilesUploaded = (files) => {
      console.log('🎬 Video files uploaded event fired!')
      
      if (files && files.length > 0) {
        const file = files[0]
        const key = file.key
        
        if (key) {
          const cdnUrl = constructCdnUrl(key)
          formData.value.video_over = cdnUrl
          
          console.log('🎬 Video URL updated:', formData.value.video_over)
          showToast('✅ Video URL set automatically!', 'success')
        } else {
          showToast('⚠️ Upload successful but could not extract key. Manual entry needed.', 'warning')
        }
      }
    }
    
    const handleVoiceOverUploaded = (files) => {
      console.log('🎤 Voice over uploaded event fired!')
      
      if (files && files.length > 0) {
        const file = files[0]
        const key = file.key
        
        if (key) {
          const cdnUrl = constructCdnUrl(key)
          formData.value.voice_over = cdnUrl
          
          console.log('🎤 Voice over URL updated:', formData.value.voice_over)
          showToast('✅ Voice over URL set automatically!', 'success')
        } else {
          showToast('⚠️ Upload successful but could not extract key. Manual entry needed.', 'warning')
        }
      }
    }
    
    // Manual URL validation
    const validateUrls = () => {
      errors.value = {}
      
      if (!formData.value.cover_photo) {
        errors.value.cover_photo = 'Cover image URL is required'
      }
      
      if (formData.value.type !== 'breathing' && !formData.value.audio_url) {
        errors.value.audio_url = 'Primary audio URL is required'
      }
      
      return Object.keys(errors.value).length === 0
    }
    
    // Save content
    const saveContent = async () => {
      if (!isFormValid.value) {
        showToast('Please fill in all required fields', 'error')
        return
      }
      
      saving.value = true
      error.value = null
      
      try {
        // Prepare data for API according to exact requirements
        const dataToSend = {
          title: formData.value.title,
          title_ar: formData.value.title_ar,
          type: formData.value.type,
          duration: 1,
          details: formData.value.details,
          details_ar: formData.value.details_ar,
          cover_photo: formData.value.cover_photo,
          main_photo: formData.value.main_photo
        }
        
        // Set display_type and media URLs based on content type
        if (formData.value.type === 'bedtime') {
          dataToSend.display_type = 'still'
          dataToSend.audio_url = formData.value.audio_url
        } else if (formData.value.type === 'breathing') {
          dataToSend.display_type = 'video'
          dataToSend.video_over = formData.value.video_over
          // For breathing exercises, audio_url should be the same as video_over
          dataToSend.audio_url = formData.value.video_over
        } else {
          // For other types (sleep_frequency, sounds_of_nature, brain_programming)
          dataToSend.display_type = 'video'
          dataToSend.audio_url = formData.value.audio_url
          dataToSend.voice_over = formData.value.voice_over
          dataToSend.video_over = formData.value.video_over
        }
        
        emit('save', {
          id: props.editingId,
          data: dataToSend,
          isEdit: !!props.editingId
        })
        
        showToast(props.editingId ? 'Content updated successfully!' : 'Content created successfully!', 'success')
        
      } catch (err) {
        error.value = err.message || 'Failed to save content'
        showToast('Failed to save content', 'error')
      } finally {
        saving.value = false
      }
    }
    
    // Helper functions
    const getTypeLabel = (type) => {
      const labels = {
        'bedtime': 'Bedtime Story',
        'breathing': 'Breathing Exercise',
        'sleep_frequency': 'Sleep Frequency',
        'souds_of_nature': 'Sounds of Nature',
        'brain_programming': 'Brain Programming'
      }
      return labels[type] || type
    }
    
    const showToast = (message, type = 'success') => {
      toast.value.show = true
      toast.value.message = message
      toast.value.type = type
      
      setTimeout(() => {
        toast.value.show = false
      }, 3000)
    }
    

    
    // Watch for type changes to clear relevant fields
    watch(() => formData.value.type, (newType, oldType) => {
      if (newType === 'breathing') {
        // Clear audio fields when switching to breathing
        formData.value.audio_url = ''
        formData.value.audio_url_ar = ''
        formData.value.voice_over = ''
      } else if (oldType === 'breathing') {
        // Clear video fields when switching from breathing
        formData.value.video_over = ''
        formData.value.video_over_ar = ''
      }
      
      if (newType === 'bedtime') {
        formData.value.voice_over = ''
        formData.value.video_over = ''
      }
    })
    
    return {
      currentStep,
      saving,
      error,
      errors,
      formData,
      toast,
      isStep1Valid,
      isStep2Valid,
      isFormValid,
      nextStep,
      prevStep,
      goBack,
      handleCoverImageUploaded,
      handleMainPhotoUploaded,
      handleAudioFilesUploaded,
      handleVideoFilesUploaded,
      handleVoiceOverUploaded,
      validateUrls,
      saveContent,
      getTypeLabel,
      showToast,
      toolbarOptions,
      arabicToolbarOptions
    }
  }
}
</script>

<style scoped>
.sleep-item-creator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.creator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.progress-indicator {
  margin-bottom: 3rem;
}

.progress-steps {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step::after {
  content: '';
  position: absolute;
  top: 20px;
  left: 60px;
  width: 80px;
  height: 2px;
  background-color: #e2e8f0;
  z-index: -1;
}

.step:last-child::after {
  display: none;
}

.step.completed::after {
  background-color: #10b981;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.step.active .step-number {
  background-color: #6366f1;
  color: white;
}

.step.completed .step-number {
  background-color: #10b981;
  color: white;
}

.step-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.step.active .step-label {
  color: #1e293b;
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  color: #dc2626;
  margin-bottom: 2rem;
}

.close-error {
  margin-left: auto;
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
}

.close-error:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

.form-step {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.step-content {
  padding: 2rem;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: span 2;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.required {
  color: #dc2626;
}

.form-input,
.form-textarea {
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input.error,
.form-textarea.error {
  border-color: #dc2626;
}

.error-text {
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: 0.25rem;
}

.media-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.media-section {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.media-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.review-content {
  margin-bottom: 2rem;
}

.review-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
}

.review-header {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.review-image {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 0.5rem;
}

.review-image-placeholder {
  width: 120px;
  height: 80px;
  background: #e2e8f0;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 0.75rem;
}

.review-info {
  flex: 1;
}

.review-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.review-title-ar {
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 0.5rem;
  direction: rtl;
}

.review-type {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #6366f1;
  color: white;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.review-details {
  padding: 1.5rem;
}

.review-description {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.review-media strong {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.media-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.media-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.875rem;
}

.no-media {
  color: #64748b;
  font-style: italic;
  font-size: 0.875rem;
}

.step-actions {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: #6366f1;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1rem;
}

/* Icons */
[class^="icon-"] {
  display: inline-block;
  width: 1em;
  height: 1em;
}

.icon-arrow-left:before { content: "←"; }
.icon-arrow-right:before { content: "→"; }
.icon-save:before { content: "💾"; }
.icon-spinner:before { content: "⟳"; }
.icon-error:before { content: "⚠️"; }
.icon-close:before { content: "✕"; }
.icon-success:before { content: "✅"; }
.icon-info:before { content: "ℹ️"; }
.icon-image:before { content: "🖼️"; }
.icon-audio:before { content: "🔊"; }

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Toast notification */
.toast-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  display: flex;
  align-items: center;
  animation: slideUp 0.3s ease forwards;
  max-width: 400px;
}

.toast-notification.success {
  border-left: 4px solid #10b981;
}

.toast-notification.error {
  border-left: 4px solid #dc2626;
}

.toast-notification.info {
  border-left: 4px solid #0ea5e9;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toast-content span {
  font-size: 0.9rem;
  color: #1e293b;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Content type specific styles */
.content-type-media {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.image-upload-group {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.upload-group {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

.image-preview {
  max-width: 200px;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-img {
  width: 100%;
  height: auto;
  display: block;
}

/* Quill Editor Styles */
.quill-wrapper {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.2s;
}

.quill-wrapper:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.quill-wrapper.error {
  border-color: #dc2626;
}

.quill-wrapper.rtl {
  direction: rtl;
}

.quill-wrapper .ql-editor {
  min-height: 120px;
  font-size: 0.875rem;
  line-height: 1.5;
}

.quill-wrapper.rtl .ql-editor {
  text-align: right;
  direction: rtl;
}

.quill-wrapper .ql-toolbar {
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.quill-wrapper.rtl .ql-toolbar {
  direction: ltr; /* Keep toolbar LTR for better UX */
}

.quill-wrapper .ql-container {
  border: none;
  font-family: inherit;
}

.quill-wrapper .ql-editor.ql-blank::before {
  font-style: normal;
  color: #9ca3af;
}

.quill-wrapper.rtl .ql-editor.ql-blank::before {
  text-align: right;
}

/* HTML content display styles */
.html-content {
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  font-size: 0.875rem;
  line-height: 1.6;
}

.html-content.rtl-content {
  direction: rtl;
  text-align: right;
}

.html-content h1,
.html-content h2,
.html-content h3 {
  margin: 0.5rem 0;
  font-weight: 600;
}

.html-content p {
  margin: 0.5rem 0;
}

.html-content ul,
.html-content ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.html-content.rtl-content ul,
.html-content.rtl-content ol {
  padding-left: 0;
  padding-right: 1.5rem;
}

.html-content blockquote {
  margin: 0.5rem 0;
  padding: 0.5rem 1rem;
  border-left: 3px solid #6366f1;
  background-color: rgba(99, 102, 241, 0.05);
  font-style: italic;
}

.html-content.rtl-content blockquote {
  border-left: none;
  border-right: 3px solid #6366f1;
}

.html-content code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
}

.html-content a {
  color: #6366f1;
  text-decoration: underline;
}

.html-content a:hover {
  color: #4f46e5;
}

/* Additional utility classes */
.mt-4 {
  margin-top: 1rem;
}

.text-sm {
  font-size: 0.875rem;
}

.text-gray-600 {
  color: #4b5563;
}

.text-xs {
  font-size: 0.75rem;
}

.font-semibold {
  font-weight: 600;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.ml-4 {
  margin-left: 1rem;
}

.p-4 {
  padding: 1rem;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.rounded {
  border-radius: 0.25rem;
}

/* Form styling */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input.error {
  border-color: #dc2626;
}

.error-text {
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: 0.25rem;
}

.help-text {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.url-input-group {
  margin-bottom: 1rem;
}

.image-preview {
  max-width: 300px;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-img {
  width: 100%;
  height: auto;
  display: block;
}

/* Responsive design */
@media (max-width: 768px) {
  .sleep-item-creator {
    padding: 1rem;
  }
  
  .creator-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .progress-steps {
    gap: 1rem;
  }
  
  .step::after {
    width: 40px;
    left: 50px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .form-group.full-width {
    grid-column: span 1;
  }
  
  .media-grid {
    grid-template-columns: 1fr;
  }
  
  .review-header {
    flex-direction: column;
  }
  
  .step-actions {
    flex-direction: column;
  }
}
</style> 